import { animate, stagger, inView } from 'motion'


//
// region constants
//

const ANIMATION_DURATION:number = 0.55
const ANIMATION_EASING = 'easeOut' as const
const STAGGER_DELAY:number = 0.1
const TRANSLATE_Y:string = '24px'
const OBSERVER_THRESHOLD:number = 0.15

// endregion


//
// initialization
//

document.addEventListener( 'DOMContentLoaded', () => {
	setupHeroAnimation()
	setupThemeAnimations()
	setupPaginationAnimation()
	setupCTAClickHandlers()
})


//
// region hero
//

/** Hero (Epic) entrance animation — fade-in + translate-up cascade on page load */
function setupHeroAnimation():void {
	const selector:string = '.brand-pitch-container .animate-on-scroll'
	if( document.querySelector( selector ) === null )
		return
	
	animate(
		selector,
		{ opacity: [0, 1], transform: [`translateY(${ TRANSLATE_Y })`, 'none'] },
		{ duration: ANIMATION_DURATION, ease: ANIMATION_EASING, delay: stagger( STAGGER_DELAY ) },
	)
}

// endregion


//
// region themes
//

/** Theme sections — each sub-part animates independently on scroll */
function setupThemeAnimations():void {
	
	// theme header: icon (scale-up) + title/subtitle (fade-in cascade)
	document.querySelectorAll( 'main > section' ).forEach( section => {
		
		// icon — fade-in + scale-up
		const icon:Element | null = section.querySelector( '.theme-icon.animate-on-scroll' )
		if( icon ){
			inView( icon, () => {
				animate(
					icon,
					{ opacity: [0, 1], transform: ['scale(0.6)', 'none'] },
					{ duration: ANIMATION_DURATION, ease: ANIMATION_EASING },
				)
			}, { amount: OBSERVER_THRESHOLD })
		}
		
		// title + subtitle — staggered fade-in + translate-up
		const headings:string = 'h1.animate-on-scroll, h2.animate-on-scroll'
		if( section.querySelector( headings ) ){
			inView( section, () => {
				animate(
					section.querySelectorAll( headings ),
					{ opacity: [0, 1], transform: [`translateY(${ TRANSLATE_Y })`, 'none'] },
					{ duration: ANIMATION_DURATION, ease: ANIMATION_EASING, delay: stagger( STAGGER_DELAY ) },
				)
			}, { amount: OBSERVER_THRESHOLD })
		}
		
		// showcase media — fade-in + translate-up + subtle scale
		const showcase:Element | null = section.querySelector( '.showcase-image.animate-on-scroll, .video-player.animate-on-scroll' )
		if( showcase ){
			inView( showcase, () => {
				animate(
					showcase,
					{ opacity: [0, 1], transform: [`translateY(${ TRANSLATE_Y }) scale(0.97)`, 'none'] },
					{ duration: ANIMATION_DURATION * 1.2, ease: ANIMATION_EASING },
				)
			}, { amount: OBSERVER_THRESHOLD })
		}
		
		// fragments — per-row stagger on desktop, individual on mobile
		setupFragmentAnimations( section )
		
		// theme CTA — fade-in + translate-up
		const cta:Element | null = section.querySelector( '.theme-cta.animate-on-scroll' )
		if( cta ){
			inView( cta, () => {
				animate(
					cta,
					{ opacity: [0, 1], transform: [`translateY(${ TRANSLATE_Y })`, 'none'] },
					{ duration: ANIMATION_DURATION, ease: ANIMATION_EASING },
				)
			}, { amount: OBSERVER_THRESHOLD })
		}
	})
}


/** Fragment cards — staggered per row (desktop) or individual (mobile) */
function setupFragmentAnimations( section:Element ):void {
	const isDesktop:boolean = window.matchMedia( '(min-width: 768px)' ).matches
	
	if( isDesktop ){
		// observe the row container, stagger its children
		const row:Element | null = section.querySelector( '.row' )
		if( row && row.querySelector( '.fragment-container.animate-on-scroll' ) ){
			inView( row, () => {
				animate(
					row.querySelectorAll( '.fragment-container.animate-on-scroll' ),
					{ opacity: [0, 1], transform: [`translateY(${ TRANSLATE_Y })`, 'none'] },
					{ duration: ANIMATION_DURATION, ease: ANIMATION_EASING, delay: stagger( STAGGER_DELAY ) },
				)
			}, { amount: OBSERVER_THRESHOLD })
		}
	} else {
		// observe each fragment individually
		section.querySelectorAll( '.fragment-container.animate-on-scroll' ).forEach( fragment => {
			inView( fragment, () => {
				animate(
					fragment,
					{ opacity: [0, 1], transform: [`translateY(${ TRANSLATE_Y })`, 'none'] },
					{ duration: ANIMATION_DURATION, ease: ANIMATION_EASING },
				)
			}, { amount: OBSERVER_THRESHOLD })
		})
	}
}

// endregion


//
// region pagination
//

/** Pagination — Previous slides in from left, Next slides in from right */
function setupPaginationAnimation():void {
	const pagination:Element | null = document.querySelector( '.pagination' )
	if( !pagination )
		return
	
	// previous button — slide from left
	const prevButton:Element | null = pagination.querySelector( '.col-6:first-child .pagination-button.animate-on-scroll' )
	if( prevButton ){
		inView( prevButton, () => {
			animate(
				prevButton,
				{ opacity: [0, 1], transform: ['translateX(-40px)', 'none'] },
				{ duration: ANIMATION_DURATION, ease: ANIMATION_EASING },
			)
		}, { amount: OBSERVER_THRESHOLD })
	}
	
	// next button — slide from right
	const nextButton:Element | null = pagination.querySelector( '.col-6:last-child .pagination-button.animate-on-scroll' )
	if( nextButton ){
		inView( nextButton, () => {
			animate(
				nextButton,
				{ opacity: [0, 1], transform: ['translateX(40px)', 'none'] },
				{ duration: ANIMATION_DURATION, ease: ANIMATION_EASING },
			)
		}, { amount: OBSERVER_THRESHOLD })
	}
}

// endregion


//
// region CTA interactions
//

/** Theme CTA buttons — navigate to data-href on click */
function setupCTAClickHandlers():void {
	document.querySelectorAll( '.theme-cta .action-button[data-href]' ).forEach( button => {
		button.addEventListener( 'click', () => {
			const href:string|null = button.getAttribute( 'data-href' )
			if( href )
				window.location.href = href
		})
	})
}

// endregion
